import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Note } from 'types';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import { getDateTimeLocal } from 'utils/getDateTimeLocal';

import actionTypes from 'constants/actionTypes';

import { setModal } from 'modules/_shared/redux/modalActions';
import { createNote } from 'modules/notes/api/createNote';

import { updateNote } from 'modules/notes/api/updateNote';
import { AddNoteModalContextType } from '../types/AddNoteModalContextType';
import { addNoteModalInitValue } from '../constants/addNoteModalInitValue';

const AddNoteModalContext = createContext<AddNoteModalContextType>(
  addNoteModalInitValue
);

export const AddNoteModalProvider: FC<{
  children: ReactNode;
  referenceId: string;
  referenceType: string;
  modalActionType: string;
  noteToEdit?: Note;
  onSuccess?: () => void;
}> = ({
  children,
  referenceId,
  referenceType,
  modalActionType,
  noteToEdit,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const [date, setDate] = useState<string>(
    getDateTimeLocal(noteToEdit?.date) || ''
  );
  const [text, setText] = useState<string>(noteToEdit?.text || '');
  const [hasBeenModified, setHasBeenModified] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeDate = (event: ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
    setHasBeenModified(true);
  };

  const handleChangeText = (value: string) => {
    setText(value);
    setHasBeenModified(true);
  };

  const handleSaveNote = async () => {
    try {
      setIsLoading(true);

      const noteData = {
        date,
        text,
        reference: referenceType,
        referenceId,
        society: actualSociety?._id,
        userId: user._id,
      };

      if (modalActionType === actionTypes.EDIT && noteToEdit?._id) {
        await store.dispatch(
          updateNote({
            noteId: noteToEdit?._id,
            updateNoteData: { ...noteData },
          })
        );
      } else {
        await store.dispatch(createNote({ note: noteData }));
      }

      onSuccess?.();
      setIsLoading(false);

      const successMessage =
        modalActionType === actionTypes.EDIT
          ? t('AnnotationUpdatedSuccessfully')
          : t('AnnotationAddedSuccessfully');

      successSwal(successMessage);
    } catch (error) {
      setIsLoading(false);

      const errorMessage =
        modalActionType === actionTypes.EDIT
          ? t('ErrorUpdatingAnnotation')
          : t('ErrorAddingAnnotation');

      errorSwal(errorMessage);
    } finally {
      handleCloseModal();
    }
  };

  return (
    <AddNoteModalContext.Provider
      value={{
        data: {
          date,
          text,
          modalActionType,
          hasBeenModified,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeDate,
          handleChangeText,
          handleSaveNote,
        },
      }}
    >
      {children}
    </AddNoteModalContext.Provider>
  );
};

export const useAddNoteModal = () => {
  const context = useContext(AddNoteModalContext);
  if (!context) {
    throw new Error(
      'useAddNoteModal must be used within a AddNoteModalProvider'
    );
  }
  return context;
};
